import App from './App.vue'
import { baseSetup } from './baseSetup'

import { createBeforeSendHandler } from '@gcx-si/vue-components/createBeforeSendHandler'
import { monkeyPatchDedupe } from '@gcx-si/vue-components/monkeyPatchDedupe'
import { createBeforeSendTransactionHandler } from '@gcx-si/vue-components/createBeforeSendTransactionHandler'
import {
  init as initSentry,
  browserTracingIntegration,
  replayIntegration,
  extraErrorDataIntegration,
  httpClientIntegration
} from '@sentry/vue'
import { createApp } from 'vue'
import { configure } from 'vee-validate'

import type { Integration } from '@sentry/types'

import { config } from '@/globals/config'
import Router from '@/router'
import { AuthenticationPlugin } from '@/shared/authentication'
import I18n from '@/App.i18n'
import { useCustomErrorHandler } from '@/shared/composables/useServerSideValidation.ts'

configure({
  generateMessage: ({ field, rule }): string => {
    if (rule.name === 'required') {
      return `${field} is required!` // custom required field message
    }
    return `${field} is invalid!` // fallback message for other rules
  },
})

const app = createApp(App)
const { customErrorHandler } = useCustomErrorHandler(app)

initSentry({
  app,
  dsn: 'https://6d4b3d10acfa43298fceffa6b0cf4045@o469121.ingest.sentry.io/6353229',
  // do not configure release here, see vite.config.ts
  logErrors: false,
  maxBreadcrumbs: 50,
  normalizeDepth: 10,
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: 1.0,
  tracesSampleRate: 0.2,
  environment: config.SENTRY_ENVIRONMENT,
  debug: false,
  beforeSend: createBeforeSendHandler(app, import.meta.env.DEV, customErrorHandler),
  beforeSendTransaction: createBeforeSendTransactionHandler(import.meta.env.DEV),
  sendDefaultPii: true,
  integrations: function (integrations) {
    monkeyPatchDedupe(integrations)

    return [
      ...integrations,
      httpClientIntegration(),
      extraErrorDataIntegration(),
      browserTracingIntegration({
        router: Router,
      }),
      replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        maxReplayDuration: 120_000,
        block: [
          '[data-sentry-block]',
        ],
      }),
    ] as Integration[]
  },
})

const setup = baseSetup(app)

setup
  .use(I18n)
  .use(Router)
  .use(AuthenticationPlugin)
  .mount('#app')
